import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Button } from "@/shared/components/shadcn/Button";
import { getSupportId } from "./telemetry";

export function CrashScreen({ error, scope }: { error: unknown; scope: "app" | "page" }) {
  const supportId = getSupportId(error);
  const content = (
    <ErrorState
      title="Something went wrong"
      message="Froppio Forms ran into an unexpected problem. Reload the app to continue. If it keeps happening, share this support ID with us."
      retryLabel="Reload app"
      onRetry={() => window.location.reload()}
    >
      <SupportId value={supportId} />
    </ErrorState>
  );
  return scope === "app"
    ? <div className="mx-auto flex min-h-dvh max-w-lg items-center p-4">{content}</div>
    : <div className="mx-auto max-w-lg p-4">{content}</div>;
}

function SupportId({ value }: { value: string }) {
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    clearTimeout(timer.current);
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(value);
      setStatus("copied");
      timer.current = setTimeout(() => setStatus("idle"), 2000);
    } catch {
      setStatus("failed");
    }
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-center gap-2">
        <span>Support ID: <code className="select-all font-mono" data-testid="support-id">{value}</code></span>
        <Button variant="quiet" size="sm" aria-label="Copy support ID" onClick={() => void copy()}>
          {status === "copied" ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {status === "copied" ? "Copied" : "Copy"}
        </Button>
      </div>
      {status === "failed" && <p>Couldn't copy. Select the support ID and copy it manually.</p>}
    </div>
  );
}
